/**
 * ChoiceEditor — Authoring UI for scene-ending choices (spec 008).
 *
 * Reads/writes via sceneStore choice actions:
 *   addChoice, updateChoice, removeChoice, clearChoices, setChoiceSettings
 *
 * Renders inside the left "Elements" panel of SceneEditor, below Sounds.
 * While one of its inputs has focus the canvas shows the choice panel
 * (CanvasChoicePanel); clicking an option there focuses its input here.
 */

import React from 'react';
import { useSceneStore } from '../../../stores/sceneStore';
import { MAX_CHOICES, CHOICE_TEXT_MAX } from '../../../../../Shared/src/utils/choices';
import { CHOICE_EDITOR_ATTR } from './CanvasChoicePanel';
import { clampChoiceText } from './choiceSettingsParse';
import ChoiceSettingsPanel from './ChoiceSettingsPanel';
import styles from './ChoiceEditor.module.scss';

const ChoiceEditor: React.FC = () => {
  const choices = useSceneStore((s) => s.scene?.choices);
  const addChoice = useSceneStore((s) => s.addChoice);
  const updateChoice = useSceneStore((s) => s.updateChoice);
  const removeChoice = useSceneStore((s) => s.removeChoice);
  const clearChoices = useSceneStore((s) => s.clearChoices);

  const hasChoices = !!choices && choices.length > 0;

  return (
    <div className={styles.choiceEditor} {...{ [CHOICE_EDITOR_ATTR]: '' }}>
      {!hasChoices ? (
        <button className={styles.addChoicesButton} onClick={addChoice}>
          + Add Choices
        </button>
      ) : (
        <>
          <div className={styles.choiceList}>
            {choices.map((choice, index) => {
              const needsLabel = choice.text.trim() === '';
              return (
                <div key={choice.id} className={styles.choiceRow}>
                  <span className={styles.choiceIndex}>{index + 1}</span>
                  <div className={styles.choiceField}>
                    <input
                      type="text"
                      className={`${styles.choiceInput} ${needsLabel ? styles.choiceInputEmpty : ''}`}
                      data-testid="choice-option-input"
                      data-choice-id={choice.id}
                      aria-label={`Option ${index + 1}`}
                      aria-invalid={needsLabel}
                      value={choice.text}
                      maxLength={CHOICE_TEXT_MAX}
                      placeholder={`Option ${index + 1}`}
                      onChange={(e) => updateChoice(choice.id, { text: clampChoiceText(e.target.value) })}
                      onBlur={(e) => {
                        const trimmed = e.target.value.trim();
                        if (trimmed !== choice.text) updateChoice(choice.id, { text: trimmed });
                      }}
                    />
                    {needsLabel && <span className={styles.choiceHint}>Needs a label</span>}
                  </div>
                  <button
                    className={styles.removeButton}
                    onClick={() => removeChoice(choice.id)}
                    title="Remove option"
                    aria-label={`Remove option ${index + 1}`}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>

          {choices.length < MAX_CHOICES && (
            <button className={styles.addOptionButton} onClick={addChoice}>
              + Add Option
            </button>
          )}

          <ChoiceSettingsPanel />

          <button className={styles.removeAllButton} onClick={clearChoices}>
            Remove All Choices
          </button>
        </>
      )}
    </div>
  );
};

export default ChoiceEditor;
